<template>
  <div class="blood">
    <Table
      :headers="headers"
      @searchByInput="$emit('search', $event)"
      @filer:update="$emit('filter', $event)"
      hideDefaultFooter
    >
      <template v-slot:body>
        <template v-for="itemGroup in (markersData || [])">
          <tr :key="itemGroup.id" class="group-line">
            <td :colspan="markersData.length <= 5 ? 5 : markersData.length">
              <div class="d-flex align-center blood-group"
                   :style="disableToggle ? 'cursor: default' : ''"
                   @click="!disableToggle && toggleRow(itemGroup.name)"
              >
                <ChevronGreenIcon
                  v-if="!disableToggle"
                  class="chevron-icon"
                  :class="{ rotated: !expandedRows?.includes(itemGroup.name) }"
                />
                <span class="group-title-text">{{itemGroup.name}}</span>
                <span class="circle dark-green-color">{{ getMarkersCount(itemGroup) }}</span>
              </div>
            </td>
          </tr>
          <template v-for="(marker, markerIndex) in (itemGroup.markers.$values || itemGroup.markers)">
            <tr :key="`marker-${marker.markerId || markerIndex}`"
                v-if="expandedRows?.includes(itemGroup.name)"
                :class="{ 'highlighted-row': activeRowIndex === markerIndex && activeGroupName === itemGroup.name }"
                style="height: 32px;">
              <td style="text-align: left;">
                <span class="marker-name" :class="{ 'marker-name-bold': !!marker.value }">
                  {{marker.markerName}}
                </span>
              </td>
              <td class="blood-above-below"
                  @click="$emit('activate-input', { marker, group: itemGroup, index: markerIndex })"
                  :class="getAboveBelowClass(marker)"
              >
                <div class="blood-above-below__inner">
                  <template v-if="!isReadOnly && currentEditingId === marker.id">
                    <Input outlined autofocus ref="computedInput" height="32px"
                           @keypress="filterInputData($event, { min: 0, max: 2000 }, 'number')"
                           :value="marker.value"
                           style="width: 100%"
                           isDynamicRenderInput
                           enableArrowNavigation
                           @blur="emitUpdate($event, { close: true })"
                           @keyup.enter="emitUpdate($event, { next: true })"
                           @keyup.tab="emitUpdate($event, { next: true })"
                           @arrow-up="emitUpdate($event, { arrowUp: true })"
                           @arrow-down="emitUpdate($event, { arrowDown: true })"
                           @shift="emitUpdate($event, { prev: true })"
                    >
                      <div slot="append" style="cursor: pointer" @click.stop="$emit('clear-marker', marker.id)">
                        <CloseIcon />
                      </div>
                    </Input>
                  </template>
                  <template v-else>
                    <span class="lab-optimal-value">
                      <template v-if="marker.isLabAbove || marker.isLabBelow">
                        <LabAboveIcon v-if="marker.isLabAbove" />
                        <LabBelowIcon v-if="marker.isLabBelow" />
                      </template>
                      <template v-else-if="marker.isOptimalAbove || marker.isOptimalBelow">
                        <OptimalAboveIcon v-if="marker.isOptimalAbove" />
                        <OptimalBelowIcon v-if="marker.isOptimalBelow" />
                      </template>
                       {{ formatValue(marker.value) }}
                    </span>
                  </template>
                </div>
              </td>
              <td style="text-align: center" class="optimal-range optimal-lab-units">
                <span>{{ formatValue(marker.optimalRangeMin) }} - {{ formatValue(marker.optimalRangeMax) }}</span>
              </td>
              <td style="text-align: center; border-left: 1px solid rgba(235,235,235,0.46); border-right: 1px solid rgba(235,235,235,0.46);" class="optimal-lab-units">
                <span>{{ formatValue(marker.labRangeMin) }} - {{ formatValue(marker.labRangeMax) }}</span>
              </td>
              <td style="text-align: center" class="optimal-lab-units units">
                <span>{{marker.units}}</span>
              </td>
            </tr>
          </template>
        </template>
      </template>
    </Table>
  </div>
</template>

<script>
import ChevronGreenIcon from '@/assets/icons/components/chevron-green.svg'
import CloseIcon from '@/assets/icons/components/close.svg'
import LabAboveIcon from '@/assets/icons/components/lab-above.svg'
import LabBelowIcon from '@/assets/icons/components/lab-below.svg'
import OptimalAboveIcon from '@/assets/icons/components/optimal-above.svg'
import OptimalBelowIcon from '@/assets/icons/components/optimal-below.svg'
import Input from '@/components/elements/Input'
import Table from '@/components/elements/Table'
import NumberMixin from '@/mixins/Number'

export default {
  name: 'BloodMarkersTable',
  mixins: [ NumberMixin ],
  components: {
    Input, Table, ChevronGreenIcon, CloseIcon,
    LabAboveIcon, LabBelowIcon, OptimalAboveIcon, OptimalBelowIcon
  },
  props: {
    headers: Array,
    markersData: Array,
    isReadOnly: Boolean,
    currentEditingId: [String, Number],
    activeRowIndex: [Number, Boolean],
    activeGroupName: String,
    expandedRows: Array,
    disableToggle: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    markersData: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val) && this.expandedRows) {
          if (this.expandedRows.length === 0) {
            val.forEach(group => {
              if (!this.expandedRows.includes(group.name)) {
                this.$emit('toggle-group', group.name);
              }
            });
          }
        }
      }
    }
  },
  methods: {
    emitUpdate(event, flags) {
      const val = (event && event.target) ? event.target.value : event
      this.$emit('update-marker', { value: val, ...flags })
    },
    toggleRow(name) {
      this.$emit('toggle-group', name)
    },
    getAboveBelowClass(marker) {
      if (this.currentEditingId === marker.id) return ''

      if (marker.isLabAbove || marker.isLabBelow) {
        return 'isLabAboveOrBelow'
      }

      if (marker.isOptimalAbove || marker.isOptimalBelow) {
        return 'isOptimalAboveOrBelow'
      }

      return ''
    },
    focusInput() {
      this.$nextTick(() => {
        const inputEl = this.$el.querySelector('input[type="text"]')
        if (inputEl) {
          inputEl.focus()
          inputEl.select()
        }
      })
    },
    getMarkersList(group) {
      return group?.markers?.$values || group?.markers || [];
    },
    getMarkersCount(group) {
      return this.getMarkersList(group).length;
    },
    formatValue(val) {
      if (val === null || val === undefined) return '';
      return parseFloat(Number(val).toFixed(2).replace(/\.?0+$/, ''));
    }
  }
}
</script>

<style lang="scss" scoped>
.dark-green-color {
  color: $dark-green;
}

.blood {
  position: relative;

  .table {
    ::v-deep .v-data-table {
      &__wrapper {
        overflow-x: hidden !important;

        table {
          thead {
            tr:last-child {
              th {
                border-bottom: 1px solid #EDF2F7;

                &:not(:last-child) {
                  border-right: 1px solid rgba(235,235,235,0.46);
                }
              }
            }
          }
        }
      }

      .v-data-table-header {
        tr {
          th {
            & > div:not(.search-filter) {
              width: 100%!important;
              justify-content: center!important;
            }

            div.v-input__slot {
              width: 100%!important;
            }

            div.search-filter {
              width: 100%;

              .filter-activator {
                margin: auto;
              }
            }

            div#filter-activator-2 {
              width: 130px!important;
              margin-left: 5px!important;
            }

            .v-input__append-inner {
              width: unset!important;
            }
          }
        }
      }

      tr {
        &.highlighted-row {
          background-color: #F7FAFC;
        }

        &:not(.grouped-table) {
          td {
            border-bottom: 1px solid #EDF2F7;

            .input {
              width: 90px;

              .v-input {
                .v-input__control {
                  .v-text-field__details {
                    display: none !important;
                  }
                }
              }
            }
          }
        }
      }

      tr:not(.grouped-table):last-child {
        td {
          border-bottom: unset !important;
        }
      }

      .v-data-table-header tr th .v-input__prepend-inner {
        margin-top: 9px !important;
        width: fit-content !important;
      }
    }
  }

  .green {
    background-color: #4AAE8C;
  }

  .yellow {
    background-color: yellow;
  }

  .red {
    background-color: red;
  }

  .blood-group {
    display: flex;
    column-gap: 8px;
    padding: 10px 20px;
    cursor: pointer;
    color: $dark-green;

    ::v-deep {
      .v-icon {
        width: 16px;
        height: 16px;
      }
    }

    .chevron-icon {
      transition: transform 0.2s ease;
      transform: rotate(0deg);

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .blood-above-below {
    text-align: center;
    border-left: 1px solid #FAFAFB;
    border-right: 1px solid #FAFAFB;
    border-bottom: none !important;
    cursor: pointer;

    .blood-above-below__inner {
      height: 28px;
      width: calc(100% - 6px);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      background-color: #EBF2FA;
      box-sizing: border-box;
      margin: 0 auto;
    }

    &:not(.isLabAboveOrBelow):not(.isOptimalAboveOrBelow) {
      .lab-optimal-value {
        font-weight: 400;
      }
    }

    &.isLabAboveOrBelow.isOptimalAboveOrBelow,
    &.isLabAboveOrBelow {
      .blood-above-below__inner {
        background-color: #FDDFDD;
      }
    }

    &.isOptimalAboveOrBelow {
      .blood-above-below__inner {
        background-color: #FFEDBF;
      }
    }

    .input {
      width: 100%;

      ::v-deep {
        .v-input {
          .v-input__slot {
            border: 1px solid #A6B7D4 !important;
            background-color: #FFFFFF !important;
            box-shadow: none;
            border-radius: 4px;
            padding: 3px 15px !important;

            input {
              color: #425466 !important;
              font-family: 'Poppins', sans-serif;
              font-size: 12px;
              font-weight: 600;
            }

            .v-input__append-inner {
              height: 16px;
              width: 16px;
              right: 0;
              top: 6px;
            }
          }

          &.v-text-field input:focus {
            outline: none !important;
            box-shadow: none !important;
          }
        }

        .v-input--is-label-active:not(.login .registration) .v-input__slot {
          background-color: #FFFFFF !important;
        }
      }
    }
  }

  .marker-name {
    color: #718096;
    font-weight: 500;
    font-size: 15px;
    padding-left: 20px;
  }

  .marker-name-bold {
    font-weight: 600;
    color: #1A202C;
  }

  .lab-optimal-value {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 8px;
    color: #1A202C;
    font-size: 15px;
    font-weight: 600;
  }

  .optimal-range {
    background-color: rgb(140, 223, 179, 10%);
  }

  .optimal-lab-units {
    color: #1A202C;
    font-size: 15px;
    font-weight: 400;
    line-height: 20px;
  }

  .group-line {
    background-color: #FAFAFB;

    .group-title-text {
      font-size: 12px;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    .circle {
      width: 20px;
      height: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(0, 140, 137, 0.1);
      border-radius: 50%;
      font-size: 12px;
      font-weight: 500;
    }
  }
}
</style>
